import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { groupSets, normMedium, setBands, tempKey } from '../../utils/culture/replicates';
import { cm2ToMm2 } from '../../utils/culture/areaUnits';
import GrowthCurve from './GrowthCurve';
import ColourReadout from './ColourReadout';

/*
 * The growth of one or more plates as the server works it out
 * (routes/culture_analyses.py growth(): GET /plates/{id}/culture-analyses,
 * and the dossier's culture_growth): a measure switch (clean colony area or
 * mean radius), the curve, each plate's rates, and every reading in a table.
 *
 * curves: [{plate_id, plate_code, points: [{analysis_id, observed_at, day,
 * clean_area_cm2, colony_area_cm2, contamination_pct, contamination_detected,
 * mean_radius_mm, and since 0073 health_status, morphology_tags and
 * contaminated (marked, or judged so)}], intervals, summary}]. A point is
 * marked contaminated (⚠) either way; the table says which. Since 0101 a
 * reading read off a tray photograph (`tray`; several dishes on one) says
 * "measured on a tray" in the table, and each plate's line counts them
 * (summary.tray_count).
 *
 * `sets` (an isolate's own plates: the dossier's Analytics tab) offers a
 * replicate-set view (docs/ANALYSIS_WORKBENCH.md 10f): the plates on one medium
 * at one temperature (each curve's protocol and temperature_c) are one line,
 * their mean with ± one SD round it per day of reading (utils/culture/
 * replicates.js setBands), n at each point; a plate with no partner, or with
 * no medium or temperature, its own line. It opens on that view when a set has
 * two plates or more. However many plates there are, it is the sets that are
 * drawn, so a fifth plate is no longer only in the table.
 *
 * Each dish, one curve per side (the server's `series`; docs/LIVE_UX_PLAN.md
 * L8): obverse, reverse and not said are lines of their own, with their own
 * rates, and a replicate set is one side's. A chooser narrows the panel to one
 * side once there is more than one. Each line's colour sits beside its rates, and
 * colour change (ΔE from its first coloured reading) is a measure once a line has
 * two: all as the server measured them, comparable only under one camera and light.
 */
const fixedMeasures = ['area', 'radius'];
const sideKey = side => side || 'unsaid';
/** A plate's curves, one per side it was read on. A curve with no `series` is one side, not said. */
const bySide = curves => curves.flatMap((c) => {
  const series = c.series?.length ? c.series : [{ dish_side: null, points: c.points, intervals: c.intervals, summary: c.summary }];
  return series.map(s => ({ ...c, ...s, key: series.length > 1 ? `${c.plate_id}|${sideKey(s.dish_side)}` : c.plate_id }));
});
const fmt = (v, digits = 2) => (v === null || v === undefined ? '–' : Number(v).toFixed(digits));
const isContaminated = p => (p.contaminated ?? p.contamination_detected) === true;
/** How many of a plate's readings were read off a tray photograph. */
export const trayCount = c => c.summary?.tray_count ?? (c.points || []).filter(p => p.tray).length;

export default function GrowthPanel({ curves, testId = 'growth-panel', sets: offerSets = false }) {
  const { t } = useTranslation('cultureAnalysis');
  const [measureChosen, setMeasure] = useState('area');
  const [viewChosen, setView] = useState(null);
  const [sideChosen, setSide] = useState('all');
  const present = (curves || []).filter(c => c && c.points?.length);
  if (!present.length) return <p data-testid={testId} style={mutedStyle}>{t('growth.none')}</p>;
  const allSides = bySide(present);
  const sides = [...new Set(allSides.map(c => sideKey(c.dish_side)))];
  const manySides = sides.length > 1;
  const side = sides.includes(sideChosen) ? sideChosen : 'all';
  const list = side === 'all' ? allSides : allSides.filter(c => sideKey(c.dish_side) === side);
  const sideWord = c => t(`growth.sides.${sideKey(c.dish_side)}`);
  const lineName = c => (manySides ? t('growth.sideLabel', { plate: c.plate_code || '', side: sideWord(c) }) : c.plate_code || '');
  const anyColour = list.some(c => c.points.some(p => p.colour));
  const anySide = list.some(c => c.dish_side);
  const MEASURES = list.some(c => c.summary?.colour_change_delta_e != null) ? [...fixedMeasures, 'colour'] : fixedMeasures;
  const measure = MEASURES.includes(measureChosen) ? measureChosen : 'area';
  const unit = t({ area: 'units.cm2', radius: 'units.mm', colour: 'units.deltaE' }[measure]);
  const valueOf = p => ({ area: p.clean_area_cm2, radius: p.mean_radius_mm, colour: p.colour_delta_e }[measure]);
  // The isolate's plates by medium and temperature, side by side; one with either unsaid is a set of its own.
  const groups = offerSets ? groupSets(list, c => (normMedium(c.protocol) && tempKey(c.temperature_c) !== null
    ? `${normMedium(c.protocol)}|${tempKey(c.temperature_c)}${manySides ? `|${sideKey(c.dish_side)}` : ''}` : `plate:${c.key}`)) : [];
  const hasSets = groups.some(g => g.members.length > 1);
  const view = hasSets ? (viewChosen || 'sets') : 'plates';
  const plateSeries = c => ({
    key: c.key, label: lineName(c),
    points: c.points.map(p => ({ day: p.day, observedAt: p.observed_at, contaminated: isContaminated(p), value: valueOf(p) })),
  });
  const setOnly = g => t('growth.setLabel', { medium: g.members[0].protocol,
                                               temperature: String(Number(tempKey(g.members[0].temperature_c))) });
  const setName = g => (manySides ? t('growth.sideLabel', { plate: setOnly(g), side: sideWord(g.members[0]) }) : setOnly(g));
  const setSeries = g => (g.members.length > 1 ? {
    key: g.key, label: setName(g), set: true,
    points: setBands(g.members, valueOf).map(b => ({ day: b.day, observedAt: b.date, contaminated: b.contaminated,
                                                     value: b.mean, sd: b.sd, n: b.n })),
  } : plateSeries(g.members[0]));
  const series = view === 'sets' ? groups.map(setSeries) : list.map(plateSeries);
  // What day 0 was (growth()'s day_zero_basis): the axis names it, and plates counted differently say so.
  const bases = [...new Set(present.map(c => c.day_zero_basis || 'created_at'))];
  const daysLabel = bases.length > 1 ? t('growth.daysFrom.mixed')
    : bases[0] === 'created_at' ? t('growth.days') : t(`growth.daysFrom.${bases[0]}`);
  const labels = {
    empty: t('growth.none'), legend: view === 'sets' ? t('growth.setsLegend') : t('growth.legend'),
    contaminated: t('growth.contaminated'), band: t('growth.band'),
    days: daysLabel, dayN: day => t('growth.day', { day: fmt(day, 1) }),
    more: n => t('growth.more', { n }),
  };
  const measureName = t(`growth.measures.${measure}`);
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div role="radiogroup" aria-label={t('growth.measure')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {MEASURES.map(m => (
            <button key={m} type="button" role="radio" aria-checked={measure === m} data-testid={`growth-measure-${m}`}
                    onClick={() => setMeasure(m)} style={toggleStyle(measure === m)}>
              {t(`growth.measures.${m}`)}
            </button>
          ))}
        </div>
        {manySides && (
          <div role="radiogroup" aria-label={t('growth.sides.label')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
               data-testid="growth-sides">
            {['all', ...sides].map(v => (
              <button key={v} type="button" role="radio" aria-checked={side === v} data-testid={`growth-side-${v}`}
                      onClick={() => setSide(v)} style={toggleStyle(side === v)}>
                {t(`growth.sides.${v}`)}
              </button>
            ))}
          </div>
        )}
        {hasSets && (
          <div role="radiogroup" aria-label={t('growth.view')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['sets', 'plates'].map(v => (
              <button key={v} type="button" role="radio" aria-checked={view === v} data-testid={`growth-view-${v}`}
                      onClick={() => setView(v)} style={toggleStyle(view === v)}>
                {t(`growth.views.${v}`)}
              </button>
            ))}
          </div>
        )}
      </div>
      <GrowthCurve series={series} unit={unit} labels={labels}
                   label={view === 'sets' ? t('growth.setLabelChart', { measure: measureName, n: series.length, unit })
                     : t('growth.label', { measure: measureName, n: list.length, unit })} />
      {view === 'sets' && (
        <ul style={rateListStyle} data-testid="growth-sets">
          {groups.filter(g => g.members.length > 1).map((g) => {
            const bands = setBands(g.members, valueOf);
            const last = bands[bands.length - 1];
            return (
              <li key={g.key} data-testid="growth-set">
                {t('growth.setRow', { set: setName(g), plates: g.members.map(c => c.plate_code).join(', ') })}
                {last && <> · <bdi dir="ltr">{last.sd !== null
                  ? t('growth.setLatest', { mean: fmt(last.mean), sd: fmt(last.sd), unit, n: last.n })
                  : t('growth.setLatestOne', { mean: fmt(last.mean), unit })}</bdi></>}
              </li>
            );
          })}
        </ul>
      )}
      <ul style={rateListStyle} data-testid="growth-rates">
        {list.map(c => (
          <li key={c.key} data-testid="growth-rate" data-side={sideKey(c.dish_side)}>
            <bdi dir="ltr" style={{ fontWeight: 600 }}>{c.plate_code}</bdi>
            {manySides && <> · {sideWord(c)}</>}
            {' · '}
            {c.summary.count > 1 ? t('growth.rates', {
              n: c.summary.count, days: fmt(c.summary.span_days, 1),
              area: fmt(c.summary.area_rate_cm2_per_day, 3), radial: fmt(c.summary.radial_rate_mm_per_day, 2),
            }) : t('growth.single')}
            {c.summary.contamination_seen && <> · <span aria-hidden="true">⚠</span> {t('growth.contaminated')}</>}
            {trayCount(c) > 0 && (
              <span data-testid="growth-tray-count"> · {t('growth.trayCount', { count: trayCount(c) })}</span>
            )}
            {bases.length > 1 && (
              <span data-testid="growth-basis"> · {t(`growth.basis.${c.day_zero_basis || 'created_at'}`)}</span>
            )}
            {c.summary.colour_latest && (
              <span data-testid="growth-colour"> · <ColourReadout lab={c.summary.colour_latest} />
                {c.summary.colour_change_delta_e != null && (
                  <> · <bdi dir="ltr">{t('growth.colourChange', { delta: fmt(c.summary.colour_change_delta_e, 1) })}</bdi></>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
      {anyColour && <p data-testid="growth-colour-note" style={mutedStyle}>{t('growth.colourNote')}</p>}
      <details style={{ fontSize: 13 }} data-testid="growth-table">
        <summary style={{ cursor: 'pointer' }}>{t('growth.table')}</summary>
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {['plate', ...(anySide ? ['side'] : []), 'when', 'day', 'clean', 'radius',
                  ...(anyColour ? ['colour'] : []), 'contamination', 'health', 'morphology'].map(c => (
                  <th key={c} scope="col" style={thStyle}>{t(`growth.columns.${c}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.flatMap(c => c.points.map(p => (
                <tr key={p.analysis_id} data-testid="growth-row">
                  <td style={tdStyle}><bdi dir="ltr">{c.plate_code}</bdi></td>
                  {anySide && <td style={tdStyle} data-testid="growth-row-side">{t(`growth.sides.${sideKey(p.dish_side)}`)}</td>}
                  <td style={tdStyle}>
                    <bdi dir="ltr">{String(p.observed_at || '').slice(0, 16).replace('T', ' ')}</bdi>
                    {p.tray && <span data-testid="growth-tray"> · {t('growth.tray')}</span>}
                  </td>
                  <td style={tdStyle}><bdi dir="ltr">{fmt(p.day, 1)}</bdi></td>
                  <td style={tdStyle}>
                    <bdi dir="ltr">{fmt(p.clean_area_cm2, 3)}</bdi>
                    {cm2ToMm2(p.clean_area_cm2) != null && (
                      <small data-testid="growth-row-mm2" style={{ display: 'block', color: 'var(--c-text-muted-fg)' }}>
                        <bdi dir="ltr">{fmt(cm2ToMm2(p.clean_area_cm2), 1)} {t('units.mm2')}</bdi>
                      </small>
                    )}
                  </td>
                  <td style={tdStyle}><bdi dir="ltr">{fmt(p.mean_radius_mm, 2)}</bdi></td>
                  {anyColour && (
                    <td style={tdStyle} data-testid="growth-row-colour">
                      {p.colour ? <ColourReadout lab={p.colour} /> : '–'}
                    </td>
                  )}
                  <td style={tdStyle}>
                    {p.contamination_detected
                      ? <><span aria-hidden="true">⚠</span> <bdi dir="ltr">{fmt(p.contamination_pct, 2)}%</bdi></>
                      : t('growth.clean')}
                  </td>
                  <td style={tdStyle} data-testid="growth-health">
                    {p.health_status === 'contaminated' && <span aria-hidden="true">⚠ </span>}
                    {t(`growth.health.${p.health_status || 'unknown'}`)}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'normal', minWidth: 140 }} data-testid="growth-morphology">
                    {(p.morphology_tags || []).map((tag) => {
                      const [group, value] = tag.split(':');
                      return t(`morphology.${group}.${value}`);
                    }).join(', ') || '–'}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

const mutedStyle = { margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' };
const rateListStyle = { margin: 0, paddingInlineStart: 18, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 };
const toggleStyle = active => ({ background: active ? 'var(--c-primary-soft)' : 'var(--c-surface)', color: 'var(--c-text-fg)',
                                 border: `1px solid ${active ? 'var(--c-primary)' : 'var(--c-border-strong)'}`, borderRadius: 6,
                                 padding: '4px 10px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 400 });
const tableWrapStyle = { maxHeight: 260, overflow: 'auto', marginTop: 6, border: '1px solid var(--c-border)', borderRadius: 6 };
const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: 12 };
const thStyle = { position: 'sticky', top: 0, background: 'var(--c-surface)', textAlign: 'start', padding: '4px 8px',
                  borderBottom: '1px solid var(--c-border)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '3px 8px', borderBottom: '1px solid var(--c-border)', whiteSpace: 'nowrap' };
